import React, { Component } from 'react';
import {
  withStyles,
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  TablePagination,
  TableFooter,
} from '@material-ui/core';
import { RouteComponentProps } from 'react-router-dom';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PeopleIcon from '@material-ui/icons/People';
import LockIcon from '@material-ui/icons/Lock';
import Loader from '../../../shared/Loaders/Loader';
import Permissions from '../Permissions/Permissions.container';
import RolePageContainer from '../RolePageContainer';
import { request } from '../../../../utils/api';

const styles = (theme) => ({
  backButton: {
    marginBottom: theme.spacing(1),
    padding: 0,
    color: '#452188',
    alignSelf: 'flex-start',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    textDecoration: 'none',
    color: '#452188',
    fontSize: '0.875rem',
    fontWeight: 400,
    cursor: 'pointer',
  },
  roleInfoCard: {
    backgroundColor: '#F8F7F9',
    borderRadius: '4px',
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 2),
  },
  roleTitle: {
    fontSize: '1rem',
    color: 'rgba(0, 0, 0, 0.87)',
    marginBottom: theme.spacing(1),
  },
  roleDescription: {
    fontSize: '0.875rem',
    color: 'rgba(0, 0, 0, 0.6)',
    lineHeight: '1.5',
  },
  tabsContainer: {
    width: '100%',
  },
  tab: {
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    minWidth: '120px',
    padding: theme.spacing(1.5, 2),
    color: 'rgba(0, 0, 0, 0.8)',
    '&.Mui-selected': {
      color: '#452188',
    },
    '& svg': {
      color: 'inherit',
    },
    '& span': {
      color: 'inherit',
    },
  },
  tabsRoot: {
    '& .MuiTabs-indicator': {
      backgroundColor: '#452188',
    },
  },
  tabContent: {
    padding: theme.spacing(2, 0),
  },
  tableContainer: {
    backgroundColor: '#ffffff',
    width: '100%',
  },
  tableHead: {
    backgroundColor: '#EAE8ED',
    borderBottom: '1px solid rgba(35, 31, 41, 0.16)',
    color: 'rgba(0, 0, 0, 0.87)',
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  tableCell: {
    borderBottom: '1px solid rgba(35, 31, 41, 0.16)',
    fontSize: '0.875rem',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  resultsText: {
    fontSize: '0.875rem',
    color: 'rgba(0, 0, 0, 0.6)',
    marginBottom: theme.spacing(2),
  },
  permissionsViewContainer: {
    backgroundColor: '#ffffff',
    padding: theme.spacing(2),
    width: '100%',
    boxSizing: 'border-box',
  },
});

interface ViewProps extends RouteComponentProps<{ roleId: string }> {
  fetchRole: (roleId: string) => Promise<any>;
  showToast: (message: string) => void;
  classes: any;
}

interface RoleUser {
  id: string;
  email: string;
  name?: string;
  classRoom?: string;
  isInstructor?: boolean;
  role?: string;
  createdAt?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      sx={{ width: '100%' }}
      hidden={value !== index}
      id={`role-tabpanel-${index}`}
      aria-labelledby={`role-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </Box>
  );
};

class View extends Component<ViewProps> {
  state = {
    title: '',
    description: '',
    permissions: [],
    loading: false,
    roleId: null,
    fallbackRole: 'educator',
    tabValue: 0,
    users: [] as RoleUser[],
    usersTotalCount: 0,
    usersLimit: 10,
    usersOffset: 0,
    usersHasMore: false,
    usersLoading: false,
  };

  fetchRoleUsers = async (roleId: string, limit = 10, offset = 0) => {
    this.setState({ usersLoading: true });
    try {
      const response = await request({
        route: `roles/${roleId}/users?limit=${limit}&offset=${offset}`,
        method: 'get',
        data: {},
        dispatch: () => {},
        showLoader: false,
      });

      if (response) {
        const {
          users = [],
          totalCount = 0,
          limit: resLimit,
          offset: resOffset,
          hasMore = false,
        } = response;

        this.setState({
          users,
          usersTotalCount: totalCount,
          usersLimit: resLimit ?? limit,
          usersOffset: resOffset ?? offset,
          usersHasMore: !!hasMore,
          usersLoading: false,
        });
      } else {
        this.setState({ usersLoading: false });
      }
    } catch (error) {
      console.error('Error fetching role users:', error);
      this.setState({ usersLoading: false });
      this.props.showToast('Failed to load users for this role');
    }
  };

  fetchRoleData = async (roleId: string) => {
    this.setState({ roleId, loading: true });
    try {
      const roleData = await this.props.fetchRole(roleId);
      if (roleData) {
        // Ensure permissions is an array
        // Permissions array now contains permission keys (e.g., "material:create") instead of IDs
        const permissions = Array.isArray(roleData.permissions)
          ? roleData.permissions
          : [];

        this.setState({
          title: roleData.title || roleData.name || '',
          description: roleData.description || '',
          permissions: permissions, // Array of permission keys
          fallbackRole: roleData.fallbackRole || 'educator',
          loading: false,
        });
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error('Error fetching role data:', error);
      this.setState({ loading: false });
      this.props.showToast('Failed to load role details');
    }
  };

  componentDidMount = async () => {
    const { match } = this.props;
    const roleId = match.params.roleId;

    if (roleId) {
      await this.fetchRoleData(roleId);
      await this.fetchRoleUsers(roleId);
    }
  };

  componentDidUpdate = async (prevProps: ViewProps) => {
    const { match } = this.props;
    const roleId = match.params.roleId;
    const prevRoleId = prevProps.match.params.roleId;

    if (roleId && roleId !== prevRoleId) {
      await this.fetchRoleData(roleId);
      await this.fetchRoleUsers(roleId);
    }
  };

  handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    this.setState({ tabValue: newValue });
  };

  handleChangePage = (_event: unknown, newPage: number) => {
    const { roleId, usersLimit } = this.state;
    if (roleId) {
      const newOffset = newPage * usersLimit;
      void this.fetchRoleUsers(roleId, usersLimit, newOffset);
    }
  };

  handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { roleId } = this.state;
    const newLimit = parseInt(event.target.value, 10);
    if (roleId) {
      void this.fetchRoleUsers(roleId, newLimit, 0);
    }
  };

  handleBackClick = () => {
    this.props.history.push('/roles');
  };

  render() {
    const { classes } = this.props;
    const {
      loading,
      title,
      description,
      permissions,
      tabValue,
      fallbackRole,
      users,
      usersTotalCount,
      usersLoading,
      usersLimit,
      usersOffset,
    } = this.state;

    if (loading) {
      return <Loader />;
    }

    return (
      <RolePageContainer title={'View Role'}>
        <IconButton
          className={classes.backButton}
          onClick={this.handleBackClick}
          aria-label="back"
        >
          <Box className={classes.backLink} component="span">
            <ArrowBackIcon fontSize="small" />
            <Typography component="span">Back</Typography>
          </Box>
        </IconButton>

        <Box className={classes.roleInfoCard}>
          <Typography className={classes.roleTitle}>{title}</Typography>
          <Typography className={classes.roleDescription}>
            {description}
          </Typography>
        </Box>

        <Paper style={{ padding: '16px', marginTop: '16px' }}>
          <Typography variant="h6">Fallback Role</Typography>
          <Typography variant="body2" color="textSecondary">
            RBAC is currently available for Materials. For other modules
            (Assignments, Announcements, Common Room, Messages etc.) not yet
            supported, this role will inherit the selected fallback permissions.
          </Typography>
          <RadioGroup
            name="role"
            value={fallbackRole ?? 'educator'}
            style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}
          >
            <FormControlLabel
              checked={fallbackRole === 'educator'}
              value="educator"
              control={<Radio color="primary" disabled />}
              label="Educator"
            />
            <FormControlLabel
              checked={fallbackRole === 'student'}
              value="student"
              control={<Radio color="primary" disabled />}
              label="Student"
            />
          </RadioGroup>
        </Paper>

        <Box className={classes.tabsContainer}>
          <Tabs
            value={tabValue}
            onChange={this.handleTabChange}
            className={classes.tabsRoot}
            indicatorColor="primary"
          >
            <Tab
              className={classes.tab}
              label={
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <PeopleIcon style={{ fontSize: '1.2rem' }} />
                  <span>Users</span>
                </Box>
              }
            />
            <Tab
              className={classes.tab}
              label={
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <LockIcon style={{ fontSize: '1.2rem' }} />
                  <span>Permissions</span>
                </Box>
              }
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box className={classes.tabContent}>
            {usersLoading ? (
              <Loader />
            ) : (
              <>
                <Typography className={classes.resultsText}>
                  {usersTotalCount > 0
                    ? `Showing ${users.length} of ${usersTotalCount} results`
                    : 'No users found for this role'}
                </Typography>
                {users.length > 0 && (
                  <TableContainer className={classes.tableContainer}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.tableHead}>
                            Name
                          </TableCell>
                          <TableCell className={classes.tableHead}>
                            Email
                          </TableCell>
                          <TableCell className={classes.tableHead}>
                            Spaces
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell className={classes.tableCell}>
                              {user.name || '-'}
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {user.email}
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {user.classRoom || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            count={usersTotalCount}
                            rowsPerPage={usersLimit}
                            page={Math.floor(usersOffset / usersLimit)}
                            onPageChange={this.handleChangePage}
                            onRowsPerPageChange={this.handleChangeRowsPerPage}
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box className={classes.tabContent}>
            <Box className={classes.permissionsViewContainer}>
              <Permissions
                selectedValues={permissions}
                handleChange={() => {}} // Read-only, no change handler
                readOnly={true}
              />
            </Box>
          </Box>
        </TabPanel>
      </RolePageContainer>
    );
  }
}

export default withStyles(styles as any, { withTheme: true })(View);
